/**
 * May this user create an organization? (§8.7 — Lane C C4.)
 *
 * `platform_settings.max_free_orgs_per_user = 0` turns self-service org creation
 * off deployment-wide, and a user can also simply be at their own limit. Both
 * are ONE answer, already resolved server-side by `GET /api/orgs/create-limit`,
 * which is why this reads `canCreate` and never the number beside it:
 *
 *  - the endpoint folds the per-user count in, so "creation is off" and "you are
 *    at your limit" do not need distinguishing at the render site;
 *  - `isUnlimited(auth)` short-circuits it to `true`, so platform admins keep the
 *    button for free — a check against `maxFreeOrgs === 0` would take it away;
 *  - the unlimited branch returns `maxFreeOrgs: Infinity`, which `JSON.stringify`
 *    emits as `null`. Harmless while nobody reads the number, and a reason not to
 *    start.
 *
 * ⚠ **`undefined` means UNRESOLVED, and callers must render the button for it.**
 * This is the lane's silent-failure trap (§9.1): a falsy check treats the
 * in-flight window as a refusal, so the "New organization" button would vanish
 * for every user on every cold load and come back a moment later — invisible on
 * a dev machine, obvious on a throttled connection. Gate on `canCreate !== false`,
 * never on `!canCreate`.
 *
 * One query key for both shells, so the desktop taskbar and the phone launcher
 * share a single request and cannot disagree.
 */
import { useQuery } from '@tanstack/react-query'
import { queryRetry } from '../query-retry'
import { orgsApi } from '@papan/orgs/client'

export const ORG_CREATE_LIMIT_KEY = ['org-create-limit'] as const

/** `true` / `false` once answered, `undefined` while still in flight. */
export function useOrgCreateLimit(): boolean | undefined {
  const { data } = useQuery({
    queryKey: ORG_CREATE_LIMIT_KEY,
    queryFn: () => orgsApi.getCreateLimit(),
    staleTime: 60_000,
    // A failure must not hide the button: leaving `data` undefined keeps the
    // unresolved (fail-open) branch, which is the same direction as loading.
    retry: queryRetry,
  })
  return data?.canCreate
}
